import React from 'react';
import { useNavigate } from 'react-router-dom';
import GoogleSignIn from '../../components/Auth/GoogleSignIn';
import SEOHead from '../../components/SEO/SEOHead';
import { Sparkles, Target, Trophy, Users } from 'lucide-react';
import { useAppStore } from '../../store/appStore';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { addNotification } = useAppStore();

  const handleLoginSuccess = () => {
    addNotification({
      type: 'success',
      message: '로그인에 성공했습니다!',
      read: false
    });
    navigate('/dashboard');
  };

  const handleLoginError = (error: Error) => {
    console.error('로그인 에러:', error);
    addNotification({
      type: 'error',
      message: '구글 로그인 중 문제가 발생했습니다. 다시 시도해주세요.',
      read: false
    });
  };

  const features = [
    {
      icon: <Target className="w-6 h-6 text-sparrow-600" />,
      title: "100일 완전 챌린지",
      description: "기본 90일 + 보너스 10일로 완전한 성장을 경험하세요"
    },
    {
      icon: <Trophy className="w-6 h-6 text-emerald-600" />,
      title: "실질적 보상 시스템",
      description: "성취에 따른 포인트와 VIP 등급으로 동기부여"
    },
    {
      icon: <Users className="w-6 h-6 text-accent-600" />,
      title: "소셜 임팩트",
      description: "보상을 기부로 연결하여 사회에 기여"
    },
    {
      icon: <Sparkles className="w-6 h-6 text-diamond-600" />,
      title: "개인 맞춤 성장",
      description: "신체 + 정신 활동으로 균형잡힌 발전"
    }
  ];

  return (
    <>
      <SEOHead
        title="로그인 - Sparrow 100일 개인성장 챌린지"
        description="Sparrow에 로그인하여 100일 개인성장 챌린지를 시작하세요. 매일 90분으로 인생을 바꾸는 여정에 함께하세요."
        url="https://sparrow100.pages.dev/login"
      />
      <div className="min-h-screen bg-gradient-primary flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* 로고 및 타이틀 */}
        <div className="text-center mb-8">
          <div className="logo w-20 h-20 mx-auto mb-6 rounded-3xl shadow-lg"></div>
          <h1 className="text-3xl sm:text-4xl font-bold text-sparrow-800 mb-3">
            Sparrow
          </h1>
          <p className="text-lg text-neutral-600 mb-2">
            100일 개인성장 챌린지
          </p>
          <p className="text-sm text-neutral-500">
            매일 90분으로 인생을 바꾸는 여정
          </p>
        </div>

        {/* 로그인 카드 */}
        <div className="card-elevated mb-6">
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-sparrow-800 mb-2">
              성장 여정을 시작해보세요
            </h2>
            <p className="text-sm text-neutral-600">
              구글 계정으로 빠르고 안전하게 시작하기
            </p>
          </div>

          <div className="w-full">
            <GoogleSignIn 
              onSuccess={handleLoginSuccess}
              onError={handleLoginError}
            />
          </div>
          
          <div className="mt-4 text-center">
            <p className="text-xs text-neutral-500">
              로그인하면{' '}
              <a href="/service" className="text-sparrow-600 hover:underline">서비스 이용약관</a> 및{' '}
              <a href="/privacy" className="text-sparrow-600 hover:underline">개인정보처리방침</a>에 동의하게 됩니다.
            </p>
          </div>
        </div>

        {/* 주요 기능 소개 */}
        <div className="card-compact">
          <h3 className="text-lg font-bold text-sparrow-800 mb-4 text-center">
            ✨ 주요 기능
          </h3>
          
          <div className="space-y-4">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="flex-shrink-0 p-2 bg-white rounded-xl shadow-soft">
                  {feature.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-sparrow-800 text-sm mb-1">
                    {feature.title}
                  </h4>
                  <p className="text-xs text-neutral-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 통계 */}
        <div className="mt-6 grid grid-cols-3 gap-3">
          <div className="text-center p-3 bg-white/80 rounded-2xl">
            <div className="text-lg font-bold text-sparrow-600">100일</div>
            <div className="text-xs text-neutral-600">완전 챌린지</div>
          </div>
          <div className="text-center p-3 bg-white/80 rounded-2xl">
            <div className="text-lg font-bold text-emerald-600">2배</div>
            <div className="text-xs text-neutral-600">보너스 보상</div>
          </div>
          <div className="text-center p-3 bg-white/80 rounded-2xl">
            <div className="text-lg font-bold text-diamond-600">VIP</div>
            <div className="text-xs text-neutral-600">등급 시스템</div>
          </div>
        </div>

        {/* 푸터 */}
        <div className="mt-8 text-center">
          <p className="text-xs text-neutral-400">
            © 2024 Sparrow. 개인성장의 새로운 시작
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default Login; 